// Formatter CLP para resultados (con $ y puntos)
export const formatCLP = (valor: number) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor || 0)
}

// Formatter UF para resultados (con UF y puntos)
export const formatUF = (valor: number) => {
  return `UF ${new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor || 0)}`
}

// Formatter genérico SOLO con puntos (sin símbolo)
export const formatNumber = (valor: number) => {
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor || 0)
}

// Parse genérico: se queda solo con dígitos
export const parseNumber = (value: string): number => {
  return Number(value.replace(/[^0-9]/g, '')) || 0
}

// Función para recalcular posición del cursor después de formatear
export const calculateCursorPosition = (
  oldValue: string,
  newValue: string,
  oldCursorStart: number,
  oldCursorEnd: number,
): number => {
  const hadSelection = oldCursorStart !== oldCursorEnd
  if (hadSelection) {
    return newValue.length
  }

  const oldDigits = oldValue.replace(/\D/g, '')
  const newDigits = newValue.replace(/\D/g, '')

  if (newDigits.length === 0) {
    return 0
  }

  if (newDigits.length > oldDigits.length) {
    return newValue.length
  }

  const digitsBeforeCursor = oldValue
    .slice(0, oldCursorStart)
    .replace(/\D/g, '').length

  const targetDigitIndex = Math.min(
    digitsBeforeCursor - 1,
    newDigits.length - 1,
  )

  if (targetDigitIndex < 0) {
    return 0
  }

  let digitsSeen = 0
  for (let i = 0; i < newValue.length; i++) {
    if (/\d/.test(newValue[i])) {
      if (digitsSeen === targetDigitIndex) {
        return i + 1
      }
      digitsSeen++
    }
  }

  return newValue.length
}

// Validar y formatear RUT chileno
export const formatRUT = (value: string): string => {
  // Remover todo excepto números y K
  const cleaned = value.replace(/[^0-9kK]/g, '').toUpperCase()

  if (cleaned.length === 0) return ''
  if (cleaned.length === 1) return cleaned

  // Separar cuerpo y dígito verificador
  const dv = cleaned.slice(-1)
  const cuerpo = cleaned.slice(0, -1)

  // Formatear cuerpo con puntos
  const formattedCuerpo = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

  return `${formattedCuerpo}-${dv}`
}

export const parseRUT = (value: string): string => {
  return value.replace(/[^0-9kK]/g, '').toUpperCase()
}

export const validateRUT = (rut: string): boolean => {
  const cleaned = parseRUT(rut)

  if (cleaned.length < 2) return false

  const dv = cleaned.slice(-1)
  const cuerpo = cleaned.slice(0, -1)

  if (cuerpo.length < 7 || cuerpo.length > 8) return false

  // Calcular dígito verificador
  let suma = 0
  let multiplicador = 2

  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1
  }

  const resto = suma % 11
  const dvCalculado = resto === 0 ? '0' : resto === 1 ? 'K' : String(11 - resto)

  return dv === dvCalculado
}

// Formatear teléfono chileno
export const formatPhone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '')

  // Si empieza con 569, lo dejamos
  // Si empieza con 56, agregamos el 9
  // Si empieza con 9, agregamos 56
  // Si no, agregamos 569

  let digits = cleaned
  if (digits.startsWith('569')) {
    digits = digits.slice(3)
  } else if (digits.startsWith('56')) {
    digits = digits.slice(2)
  } else if (digits.startsWith('9')) {
    digits = digits.slice(1)
  }

  // Limitar a 8 dígitos
  digits = digits.slice(0, 8)

  if (digits.length === 0) return ''
  if (digits.length <= 4) return `+56 9 ${digits}`

  return `+56 9 ${digits.slice(0, 4)} ${digits.slice(4)}`
}

export const parsePhone = (value: string): string => {
  return value.replace(/\D/g, '')
}

export const validatePhone = (phone: string): boolean => {
  const cleaned = parsePhone(phone)
  // Debe tener 11 dígitos (56 + 9 + 8 dígitos)
  return cleaned.length === 11 && cleaned.startsWith('569')
}

// Validar email
export const validateEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
  return regex.test(email.trim())
}
